import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { rewrapDek } from "~/lib/crypto";
import type { KeyManager } from "~/lib/crypto";
import {
  linkGoalToProgramFn,
  listGoalProgramsFn,
  listProgramGoalsUnderFn,
  placeProgramGoalUnderFn,
  prepareGoalLinkFn,
  removeGoalFromProgramFn,
  unlinkGoalFromProgramFn,
} from "~/lib/functions/goal-link";

import { goalQueryKeys } from "./goalQuery";

export const goalLinkQueryKeys = {
  all: ["goal-links"] as const,
  programs: (goalId: string) =>
    [...goalLinkQueryKeys.all, "programs", goalId] as const,
  programGoalsUnder: (goalId: string) =>
    [...goalLinkQueryKeys.all, "program-goals-under", goalId] as const,
};

/** The programs a Life Design goal is in, and the client's other active programs. */
export const goalProgramsQueryOptions = (goalId: string) =>
  queryOptions({
    queryKey: goalLinkQueryKeys.programs(goalId),
    queryFn: () => listGoalProgramsFn({ data: { goalId } }),
  });

export type GoalPrograms = Awaited<ReturnType<typeof listGoalProgramsFn>>;

/** The program goals planned under a Life Design goal. */
export const programGoalsUnderQueryOptions = (goalId: string) =>
  queryOptions({
    queryKey: goalLinkQueryKeys.programGoalsUnder(goalId),
    queryFn: () => listProgramGoalsUnderFn({ data: { goalId } }),
  });

export type ProgramGoalUnder = Awaited<
  ReturnType<typeof listProgramGoalsUnderFn>
>[number];

type GoalInProgram = { goalId: string; engagementId: string };

/**
 * The client suggests her Life Design goal to a program. Her browser wraps
 * the keys of the goal and its steps for the program's coach, and the link
 * saves them in one transaction.
 */
export async function linkGoalWithCoachKeys(
  keyManager: KeyManager,
  { goalId, engagementId }: GoalInProgram,
): Promise<void> {
  const { coach, ownKeys } = await prepareGoalLinkFn({
    data: { goalId, engagementId },
  });
  const privateKey = keyManager.getPrivateKey();
  const coachKeys = coach
    ? await Promise.all(
        ownKeys.map(async (key) => ({
          resourceId: key.resourceId,
          encryptedDek: await rewrapDek(
            key.encryptedDek,
            privateKey,
            coach.publicKey,
          ),
        })),
      )
    : [];
  await linkGoalToProgramFn({ data: { goalId, engagementId, coachKeys } });
}

/** `linkGoalWithCoachKeys` from the goal panel, which then shows the new link. */
export const useLinkGoalToProgram = (keyManager: KeyManager | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (link: GoalInProgram) => {
      if (!keyManager) throw new Error("Unlock with your PIN first.");
      await linkGoalWithCoachKeys(keyManager, link);
    },
    onSuccess: (_result, { goalId }) =>
      queryClient.invalidateQueries({
        queryKey: goalLinkQueryKeys.programs(goalId),
      }),
  });
};

/** The client removes her Life Design goal from a program, or cancels her suggestion. */
export const useUnlinkGoalFromProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GoalInProgram) => unlinkGoalFromProgramFn({ data }),
    onSuccess: (_result, { goalId }) =>
      queryClient.invalidateQueries({
        queryKey: goalLinkQueryKeys.programs(goalId),
      }),
  });
};

/** The coach removes a client's Life Design goal from the program. */
export const useRemoveGoalFromProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GoalInProgram) => removeGoalFromProgramFn({ data }),
    onSuccess: (_result, { engagementId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: goalQueryKeys.byEngagement(engagementId),
        }),
        queryClient.invalidateQueries({
          queryKey: goalQueryKeys.mainByEngagement(engagementId),
        }),
      ]),
  });
};

/** The client places a program goal under one of her Life Design goals, or takes it out. */
export const usePlaceProgramGoal = (engagementId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { goalId: string; parentGoalId: string | null }) =>
      placeProgramGoalUnderFn({ data }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: goalQueryKeys.byEngagement(engagementId),
        }),
        queryClient.invalidateQueries({
          queryKey: [...goalLinkQueryKeys.all, "program-goals-under"],
        }),
      ]),
  });
};
